/**
 * SpeakerSnippetPlayer Component
 *
 * Compact audio player for speaker binding wizard snippets.
 * Displays a single audio clip with transcript text and quality indicators.
 * Allows users to listen to representative clips for speaker identification.
 */

import { useState, useRef, useCallback } from "react";
import { Play, Pause, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { AudioSnippet } from "@/types/speaker-binding";

interface SpeakerSnippetPlayerProps {
  snippet: AudioSnippet;
  audioSrc: string;
  label?: string;
  className?: string;
}

/**
 * Format milliseconds to time string (M:SS)
 */
function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Format quality percentage
 */
function formatQuality(score: number): string {
  return `${Math.round(score * 100)}%`;
}

export function SpeakerSnippetPlayer({
  snippet,
  audioSrc,
  label,
  className = "",
}: SpeakerSnippetPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const duration = snippet.duration_ms;

  // Toggle play/pause
  const togglePlay = useCallback(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        // Set the audio to start at the snippet's start time
        audioRef.current.currentTime = snippet.start_time_ms / 1000;
        audioRef.current.play();
      }
    }
  }, [isPlaying, snippet.start_time_ms]);

  // Handle time updates - stop at snippet end time
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      const currentMs = audioRef.current.currentTime * 1000;

      // If we've reached the end of the snippet, pause
      if (currentMs >= snippet.end_time_ms) {
        audioRef.current.pause();
        audioRef.current.currentTime = snippet.start_time_ms / 1000;
        setIsPlaying(false);
        setCurrentTime(0);
      } else {
        // Calculate time within snippet
        const snippetTime = currentMs - snippet.start_time_ms;
        setCurrentTime(snippetTime);
      }
    }
  }, [snippet.start_time_ms, snippet.end_time_ms]);

  // Handle play event
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  // Handle pause event
  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Handle ended event
  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  // Calculate progress percentage
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Card className={`p-4 ${className}`}>
      <audio
        ref={audioRef}
        src={audioSrc}
        onTimeUpdate={handleTimeUpdate}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
        preload="metadata"
      />

      {/* Header with label and quality indicators */}
      <div className="flex items-center justify-between mb-3">
        {label && (
          <span className="text-sm font-medium text-foreground">{label}</span>
        )}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span title="Transcription confidence">
            Confidence: {formatQuality(snippet.confidence)}
          </span>
          <span title="Audio quality score">
            Quality: {formatQuality(snippet.quality_score)}
          </span>
        </div>
      </div>

      {/* Transcript text */}
      <div className="mb-3 p-3 bg-muted rounded-lg">
        <p className="text-sm text-foreground italic">"{snippet.text}"</p>
      </div>

      {/* Compact player controls */}
      <div className="flex items-center gap-2">
        {/* Play/Pause button */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={togglePlay}
          className="shrink-0"
        >
          {isPlaying ? (
            <>
              <Pause className="h-4 w-4 mr-1" />
              Pause
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-1" />
              Play
            </>
          )}
        </Button>

        {/* Progress indicator */}
        <div className="flex-1 flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 dark:bg-blue-700 transition-all duration-100"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground tabular-nums shrink-0 min-w-[48px] text-right">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>

        {/* Volume indicator */}
        <Volume2 className="h-4 w-4 text-muted-foreground shrink-0" />
      </div>

      {/* Timestamp info */}
      <div className="mt-2 text-xs text-muted-foreground text-center">
        Timestamp: {formatTime(snippet.start_time_ms)} -{" "}
        {formatTime(snippet.end_time_ms)}
      </div>
    </Card>
  );
}
